import { without, assign, isNull, clone, isUndefined } from 'lodash-es'
import moment from 'moment'

import { setFatalError } from '../FatalError/FatalError'

import { actionNames } from './actions'

const initialState = {
  debugStats: [],
  monitoringStats: [],
  presenterQualityReports: {},
  limitTransport: null,
  freezeDebugProfilingDisplay: false,
  hasStartedConnecting: false,
  twilioConnected: false,
  twilioReconnecting: false,
  areDebugGraphsVisible: false,
  participantIds: [],
  tracksByParticipant: {},
  localTracks: [],
  isSharingScreen: false,
  role: null,
  liveId: null,
  presenterToken: null,
  isSendingStream: false,
  streamingStartTriggeredAt: null,
  streamingStartedAt: null,
  streamingStartFailed: false,
  authInfo: {},
  schedule: {},
  firebaseToken: null,
  liveAuthToken: null,
  selectedPresenterTrackId: null,
  selectedPresenterTrackName: null,
  selectedPresenterParticipantId: null,
  presenterSelectedMoment: null,
  webinarInfo: {},
  liveData: {},
  liveTogglingTriggeredAt: null,
  recordingToggleTriggeredAt: null,
  soundLevelsByTrackId: {},
  presentersData: [],
  presentersDataState: 'not_loaded',
  isEndWebinarConfirmModalVisible: false,
  siteLogo: null,
  isWebinarOverlayVisible: false,
  isEndingWebinar: false,
  webinarEnded: false,
  videoInputDeviceId: null,
  audioInputDeviceId: null,
  audioOutputDeviceId: 'default',
  criticalError: null,
  criticalErrorType: null,
  monitoringWarningIsShown: false,
  monitoringWarningClosedAt: null,
  monitoringWarningIsMuted: false,
  showSecondCameraModal: false,
  isSecondCamLoading: false,
  secondCameraToken: null,
  localParticipantName: '',
  localParticipantId: null,
  isReconnecting: false,
  isConnectingModalVisible: true,
  shouldRotateWaitingText: true,
  connectingModalText: null,
  showPermissionsHint: false,
  isSignalingServiceReady: false,
  isSecondCam: false,
  isMobileDevice: false,
  facingMode: 'environment',
  funnelData: {},
  metricsData: [],
  areWebinarOverMetricsVisible: true,
  isGearMode: false,
  isGearModePreviewInitialising: false,
  previewVideoStream: null,
  previewTrackId: null,
  overlayData: null,
  questionOverlayCommentId: null,
  persistedMutingSettings: null,
  isHeadlessHidden: true,
  isShowingCornerTagOverlay: false,
  speakersList: null,
}

const updateTrack = (action, state) => {
  const { participantId } = action
  const tracksByParticipant = assign({}, state.tracksByParticipant)

  if (isUndefined(tracksByParticipant[participantId])) {
    return state.tracksByParticipant
  }

  tracksByParticipant[participantId] = tracksByParticipant[participantId].map(
    (track) => {
      if (track.id === action.track.id) {
        // TODO (noahlaux): Refactor using weakmap to hold reference
        // eslint-disable-next-line no-param-reassign
        action.track.originParticipantId = track.originParticipantId
        return action.track
      }

      return track
    }
  )

  return tracksByParticipant
}

const mainReducer = (state = initialState, action = undefined) => {
  switch (action.type) {
    case actionNames.INIT_WEBINAR: {
      const newState = {
        ...state,
        role: action.options.role,
        liveId: action.options.liveId,
        limitTransport: action.options.limitTransport,
        isSecondCam: action.options.isSecondCam,
        isMobileDevice: action.options.isMobileDevice,
        streamingStartTriggeredAt:
          action.options.role === 'producer' ? moment() : null,
      }

      // the token is set from the URL here
      if (newState.role === 'presenter') {
        newState.presenterToken = action.options.presenterToken
      }

      return newState
    }
    case actionNames.CONNECT_TO_WEBINAR: {
      return {
        ...state,
        hasStartedConnecting: true,
      }
    }
    case actionNames.TWILIO_CONNECTED: {
      return {
        ...state,
        twilioConnected: true,
      }
    }
    case actionNames.TWILIO_DISCONNECTED: {
      return {
        ...state,
        twilioConnected: false,
      }
    }
    case actionNames.TWILIO_RECONNECTING: {
      return {
        ...state,
        twilioReconnecting: true,
      }
    }
    case actionNames.TWILIO_RECONNECTED: {
      return {
        ...state,
        twilioConnected: true,
      }
    }
    case actionNames.FETCH_CONNECTION_INFO_SUCCESS: {
      const newState = {
        ...state,
        authInfo: action.authInfo,
        firebaseToken: action.firebaseToken,
        liveAuthToken: action.liveAuthToken,
        siteLogo: action.siteLogo,
        isGearMode: action.isGearMode,
        liveSpeakerId: action.liveSpeakerId,
      }

      // the token will be used for generating the link to join as a presenter
      // in case of a producer conflict
      if (state.role === 'producer') {
        newState.presenterToken = action.presenterToken
      }

      return newState
    }
    case actionNames.PARTICIPANT_JOINED: {
      return {
        ...state,
        participantIds: [...state.participantIds, action.participantId],
      }
    }
    case actionNames.PARTICIPANT_LEAVING: {
      return {
        ...state,
        participantIds: without(state.participantIds, action.participantId),
      }
    }
    case actionNames.TRACK_ADDED: {
      const { participantId, track } = action

      const newState = {
        ...state,
        tracksByParticipant: {
          ...state.tracksByParticipant,
          [participantId]: [
            ...(state.tracksByParticipant[participantId] || []),
            track,
          ],
        },
      }

      return newState
    }
    case actionNames.TRACK_REMOVED: {
      const { participantId, track } = action

      if (state.role === 'presenter' && track.name.includes('screensharing')) {
        return {
          ...state,
        }
      }

      const tracksByParticipant = assign({}, state.tracksByParticipant)
      let newState

      if (tracksByParticipant[participantId]) {
        if (tracksByParticipant[participantId].length === 1) {
          delete tracksByParticipant[participantId]

          newState = {
            tracksByParticipant,
          }
        } else {
          newState = {
            tracksByParticipant: {
              ...tracksByParticipant,
              [participantId]: without(
                tracksByParticipant[participantId],
                track
              ),
            },
          }
        }
      }

      if (track.id === state.selectedPresenterTrackId) {
        newState.selectedPresenterTrackId = null
      }

      return {
        ...state,
        ...newState,
      }
    }
    case actionNames.TRACK_MUTED: {
      return {
        ...state,
        tracksByParticipant: updateTrack(action, state),
      }
    }
    case actionNames.TRACK_UNMUTED: {
      return {
        ...state,
        tracksByParticipant: updateTrack(action, state),
      }
    }
    case actionNames.LOCAL_TRACK_ADDED: {
      return {
        ...state,
        localTracks: [...state.localTracks, action.track],
      }
    }
    case actionNames.LOCAL_TRACK_REMOVED: {
      return {
        ...state,
        localTracks: without(state.localTracks, action.track),
      }
    }
    case actionNames.BROADCAST_STREAMING_STARTED: {
      return {
        ...state,
        streamingStartTriggeredAt: null,
        streamingStartedAt: moment(),
        isReconnecting: false,
        isGearModePreviewInitialising: true,
      }
    }
    case actionNames.BROADCAST_STREAMING_START_FAILED: {
      return {
        ...state,
        streamingStartTriggeredAt: null,
        streamingStartFailed: true,
        selectedPresenterTrackId: null,
        selectedPresenterTrackName: null,
        selectedPresenterParticipantId: null,
        streamingStartedAt: null,
      }
    }
    case actionNames.BROADCAST_STREAMING_STOPPED: {
      return {
        ...state,
        streamingStartTriggeredAt: null,
        selectedPresenterTrackId: null,
        selectedPresenterParticipantId: null,
        recordingToggleTriggeredAt: null,
        streamingStartedAt: null,
        isGearModePreviewInitialising: false,
      }
    }
    case actionNames.GEAR_MODE_PREVIEW_INITIALIZED: {
      return {
        ...state,
        isGearModePreviewInitialising: false,
      }
    }
    case actionNames.SCREEN_SHARING_STOPPED: {
      return {
        ...state,
        isSharingScreen: false,
      }
    }
    case actionNames.UPDATE_LIVE_PREVIEW: {
      return {
        ...state,
        previewVideoStream: action.videoStream || state.previewVideoStream,
        previewTrackId: action.trackId || state.previewTrackId,
      }
    }
    case actionNames.WEBINAR_INFO_FETCH_SUCCESS: {
      return {
        ...state,
        webinarInfo: action.data,
      }
    }
    case actionNames.FETCH_LIVE_DATA_SUCCESS: {
      return {
        ...state,
        liveData: action.data,
      }
    }
    case actionNames.LIVE_TOGGLE: {
      const newState = {
        ...state,
        liveTogglingTriggeredAt: action.momentDate,
      }

      if (!action.isActive) {
        newState.isEndWebinarConfirmModalVisible = false
      } else {
        newState.recordingToggleTriggeredAt = action.momentDate
      }

      return newState
    }
    case actionNames.LIVE_TOGGLE_FAILURE: {
      return {
        ...state,
        liveTogglingTriggeredAt: null,
        presenterSelectedMoment: null,
        recordingToggleTriggeredAt: null,
      }
    }
    case actionNames.LIVE_TOGGLE_SUCCESS: {
      const newState = {
        ...state,
        liveTogglingTriggeredAt: null,
        presenterSelectedMoment: action.isLive ? moment() : null,
      }

      if (!action.isLive) {
        newState.recordingToggleTriggeredAt = null
      }

      return newState
    }
    case actionNames.WEBINAR_ENDED: {
      return {
        ...state,
        streamingStartTriggeredAt: null,
        recordingToggleTriggeredAt: null,
        streamingStartedAt: null,
        isGearModePreviewInitialising: false,
        isEndingWebinar: false,
        webinarEnded: true,
        isWebinarOverlayVisible: true,
        presenterSelectedMoment: null,
        selectedPresenterParticipantId: null,
        selectedPresenterTrackId: null,
        selectedPresenterTrackName: null,
      }
    }
    case actionNames.VOLUME_LEVELS_UPDATED: {
      return {
        ...state,
        soundLevelsByTrackId: action.values,
      }
    }
    case actionNames.TOGGLE_END_WEBINAR_CONFIRM_VISIBILITY: {
      return {
        ...state,
        isEndWebinarConfirmModalVisible: action.isVisible,
      }
    }
    case actionNames.RECORDING_STARTED: {
      return {
        ...state,
        recordingToggleTriggeredAt: null,
      }
    }
    case actionNames.RECORDING_FAILED: {
      return {
        ...state,
        recordingToggleTriggeredAt: null,
      }
    }
    case actionNames.TOGGLE_WEBINAR_OVER_OVERLAY: {
      return {
        ...state,
        isWebinarOverlayVisible: !state.isWebinarOverlayVisible,
      }
    }
    case actionNames.TOGGLE_DEBUG_PROFILING_FREEZE: {
      return {
        ...state,
        freezeDebugProfilingDisplay: !state.freezeDebugProfilingDisplay,
      }
    }
    case actionNames.LIVE_PROFILING_DATA_ARRIVED: {
      const stats = clone(state.debugStats)

      if (state.debugStats.length > 200) {
        stats.shift()
      }

      stats.push(action.stats)

      const newState = {
        ...state,
        monitoringStats: stats,
      }

      if (!state.freezeDebugProfilingDisplay) {
        newState.debugStats = stats
      }

      return newState
    }
    case actionNames.ENDING_WEBINAR: {
      return {
        ...state,
        isEndingWebinar: true,
        liveData: {
          ...state.liveData,
          live_status: 'previous',
        },
      }
    }
    case actionNames.FETCH_SCHEDULE_SUCCESS: {
      const { schedule } = action
      return {
        ...state,
        schedule,
      }
    }
    case actionNames.DEVICES_CHANGED: {
      return {
        ...state,
        videoInputDeviceId: action.videoInputDeviceId,
        audioInputDeviceId: action.audioInputDeviceId,
        audioOutputDeviceId: action.audioOutputDeviceId,
      }
    }
    case actionNames.TOGGLE_FACING_MODE_SUCCESS: {
      return {
        ...state,
        facingMode: action.facingMode,
      }
    }
    case actionNames.CRITICAL_ERROR_OCCURED: {
      setFatalError(action.error, { reload: 'ask' })

      // never override an existing critical error
      if (!isNull(state.criticalError)) {
        return state
      }

      return {
        ...state,
        criticalError: action.error,
        criticalErrorType: action.errorType,
        isConnectingModalVisible: false,
        shouldRotateWaitingText: false,
      }
    }
    case actionNames.MONITORING_WARNING_OPENED: {
      return {
        ...state,
        monitoringWarningIsShown: true,
      }
    }
    case actionNames.SHOW_PERMISSIONS_HINT: {
      return {
        ...state,
        showPermissionsHint: true,
      }
    }
    case actionNames.PERMISSIONS_GRANTED: {
      return {
        ...state,
        showPermissionsHint: false,
        videoInputDeviceId: action.videoInputDeviceId,
        audioInputDeviceId: action.audioInputDeviceId,
      }
    }
    case actionNames.MONITORING_WARNING_CLOSED: {
      return {
        ...state,
        monitoringWarningIsShown: false,
        monitoringWarningClosedAt: moment(),
      }
    }
    case actionNames.MONITORING_WARNING_MUTED: {
      return {
        ...state,
        monitoringWarningIsMuted: true,
      }
    }
    case actionNames.PRESENTER_QUALITY_UPDATED: {
      return {
        ...state,
        presenterQualityReports: action.qualityByPresenter,
      }
    }
    case actionNames.SECOND_CAMERA_TRIGGERED: {
      return {
        ...state,
        isSecondCamLoading: true,
        showSecondCameraModal: true,
      }
    }
    case actionNames.SECOND_CAMERA_FETCH_CODE_SUCCESS: {
      return {
        ...state,
        isSecondCamLoading: false,
        secondCameraToken: action.token,
      }
    }
    case actionNames.SECOND_CAMERA_FETCH_CODE_ERROR: {
      return {
        ...state,
        isSecondCamLoading: false,
        showSecondCameraModal: false,
      }
    }
    case actionNames.HIDE_SECOND_CAMERA_MODAL: {
      return {
        ...state,
        showSecondCameraModal: false,
      }
    }
    case actionNames.HIDE_CONNECTING_MODAL: {
      return {
        ...state,
        isConnectingModalVisible: false,
        shouldRotateWaitingText: false,
      }
    }
    case actionNames.UPDATE_CONNECTING_MODAL_TEXT: {
      return {
        ...state,
        connectingModalText: state.isReconnecting
          ? state.connectingModalText
          : action.text,
      }
    }
    case actionNames.RECONNECT_STREAMING: {
      const newState = {
        ...state,
        isReconnecting: true,
        streamingStartTriggeredAt: moment(),
        shouldRotateWaitingText: false,
      }

      if (state.isConnectingModalVisible) {
        newState.connectingModalText =
          'This is taking longer than usual. Trying a reconnect...'
      }

      return newState
    }
    case actionNames.RECONNECT_STREAMING_SUCCESS: {
      return {
        ...state,
        isReconnecting: false,
        streamingStartTriggeredAt: null,
        streamingStartedAt: moment(),
      }
    }
    case actionNames.SIGNALING_SERVICE_INITIALIZED: {
      return {
        ...state,
        isSignalingServiceReady: true,
      }
    }
    case actionNames.FETCH_WEBINAR_OVER_METRICS_ERROR: {
      return {
        ...state,
        // hide just the metrics if something goes wrong, show the overlay otherwise
        areWebinarOverMetricsVisible: false,
      }
    }
    case actionNames.FETCH_WEBINAR_OVER_METRICS_SUCCESS: {
      return {
        ...state,
        funnelData: action.funnel,
        metricsData: action.metrics,
      }
    }
    case actionNames.OVERLAY_UPDATED: {
      return {
        ...state,
        overlayData: action.overlayData,
      }
    }
    case actionNames.PERSIST_MUTING_SETTINGS: {
      return {
        ...state,
        persistedMutingSettings: action.data,
      }
    }
    case actionNames.AFTER_PLAYBACK_MUTING_SETTINGS_RESTORED: {
      return {
        ...state,
        persistedMutingSettings: null,
      }
    }
    case actionNames.HEADLESS_HIDDEN_STATE_UPDATED: {
      return {
        ...state,
        isHeadlessHidden: action.isHidden,
      }
    }
    case actionNames.SHOW_SPEAKER_OVERLAY: {
      return {
        ...state,
        questionOverlayCommentId: null,
        isShowingCornerTagOverlay: false,
      }
    }
    case actionNames.SHOW_QUESTION_OVERLAY: {
      return {
        ...state,
        questionOverlayCommentId: action.questionOverlayCommentId,
        isShowingCornerTagOverlay: false,
      }
    }
    case actionNames.CORNER_TAG_OVERLAY_TOGGLED: {
      return {
        ...state,
        isShowingCornerTagOverlay: action.isShowing,
        questionOverlayCommentId: null,
      }
    }
    case actionNames.FETCH_SPEAKERS_SUCCESS: {
      return {
        ...state,
        speakersList: action.speakers,
      }
    }
    case actionNames.SET_PARTICIPANT_IDS: {
      return {
        ...state,
        participantIds: action.participantIds,
      }
    }
    default:
      return state
  }
}

export default mainReducer
